/**
 * `can()` — the single authorisation decision in the system.
 *
 * PURE ON PURPOSE. No database, no request context, no clock. Everything it needs arrives as
 * arguments, which is what lets the identical function run in four places that must agree:
 *
 *   - `apps/api`     route middleware (the enforcement point)
 *   - `apps/web`     route guards, nav filtering, and the gated /admin area
 *   - `apps/mobile`  navigation gating
 *   - `packages/ai`  filtering the tool list handed to the model
 *
 * The last one is the reason purity is worth insisting on. The agent is handed only the tools the
 * caller could invoke themselves, so a prompt-injected instruction to "use the payroll tool" fails
 * because the tool was never in the list — not because the model declined. Any drift between the
 * UI's idea of permission and the API's would be a bug; any drift between the API's and the AI's
 * would be a vulnerability.
 *
 * A note on the two layers. RLS in Postgres already prevents cross-tenant reads. `can()` sits
 * above it and answers a different question: within one tenant, may THIS actor touch THIS record?
 * Neither replaces the other — RLS is the backstop that survives a bug here.
 */

import type { Permission } from './permissions.js';
import { ROLE_PERMISSIONS, type Role, type Scope } from './roles.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Grant {
  readonly role: Role;
  readonly scope: Scope;
  /**
   * Required for the absolute scopes (`legal_entity`, `location`, `department`); meaningless for
   * `org`, `self`, and the two relative scopes, which are computed rather than looked up.
   */
  readonly scopeId?: string | null;
}

export interface Actor {
  readonly personId: string;
  readonly orgId: string;
  readonly grants: readonly Grant[];
  /**
   * Set when this session is an impersonation. Does not change what is permitted — the effective
   * permissions are still the impersonated person's — but every audit row records who was really
   * behind the action.
   */
  readonly impersonatorPersonId?: string | null;
  /**
   * Set when this session was minted by a platform admin's "enter organisation" grant rather than
   * a person-to-person impersonation — see `packages/db/prisma/schema.prisma`'s
   * `OrgAccessGrant`/`Session.impersonatorPlatformAdminId`. Same non-effect on `decide()` as
   * `impersonatorPersonId` above: it changes nothing about what is permitted, only who every audit
   * row attributes the action to.
   */
  readonly impersonatorPlatformAdminId?: string | null;
  /**
   * Optional dynamic role-to-permissions map loaded per organization.
   * If present, overrides static ROLE_PERMISSIONS for custom or customized roles.
   */
  readonly rolePermissions?: Record<string, ReadonlySet<Permission> | readonly Permission[]>;
  /**
   * Set by the API when the organisation's trial has expired without a signed agreement. Denies
   * every permission that is not a plain read — see `isBlockedWhenReadOnly`.
   */
  readonly readOnly?: boolean;
}

/**
 * What is being acted upon.
 *
 * `personId` is "who this record is about", not who is acting: for a leave request it is the
 * requester, for a payslip the employee. That is what makes `self` scope work without a special
 * case per resource.
 */
export interface ResourceTarget {
  readonly orgId: string;
  readonly legalEntityId?: string | null;
  readonly locationId?: string | null;
  readonly departmentId?: string | null;
  readonly personId?: string | null;
  readonly managerPersonId?: string | null;
  /**
   * Managers above `personId`, nearest first. Required for `reporting_tree` grants to resolve —
   * a manager two levels up cannot be recognised from the record alone.
   */
  readonly managerChain?: readonly string[];
}

export interface Decision {
  readonly allowed: boolean;
  /** The grant that permitted it, for audit and for debugging a surprising denial. */
  readonly via?: Grant;
  readonly reason: string;
}

// ---------------------------------------------------------------------------
// Scope resolution
// ---------------------------------------------------------------------------

function scopeCovers(actor: Actor, grant: Grant, target: ResourceTarget): boolean {
  switch (grant.scope) {
    case 'org':
      // Org equality was already checked by the caller; reaching here means it holds.
      return true;

    case 'legal_entity':
      return !!grant.scopeId && grant.scopeId === target.legalEntityId;

    case 'location':
      return !!grant.scopeId && grant.scopeId === target.locationId;

    case 'department':
      return !!grant.scopeId && grant.scopeId === target.departmentId;

    case 'reporting_tree':
      // Anywhere below the actor in the org chart, at any depth.
      return !!target.managerChain?.includes(actor.personId);

    case 'direct_reports':
      return !!target.managerPersonId && target.managerPersonId === actor.personId;

    case 'self':
      return !!target.personId && target.personId === actor.personId;
  }
}

// ---------------------------------------------------------------------------
// The decision
// ---------------------------------------------------------------------------

/**
 * Built-in roles whose permission list begins `...EMPLOYEE`, but whose job is NOT to see other people's personal records.
 *
 * Every Employee permission (`leave:read`, `goal:read`, `review:read`, `feedback:read`, `one_on_one:read`, `expense:read`,
 * `timesheet:read`, `employee:read`…) is meant for "my own", and a grant's scope applies to the whole role. So a recruiter, an IT
 * admin or an asset manager granted at ORG scope also read EVERYONE's leave, goals, reviews and expenses (copilot audit S1). For
 * these roles the Employee base stays at the Employee's own reach — SELF — except the few Employee permissions the job genuinely
 * needs across people, listed here with the reason. What the role adds beyond the Employee base keeps the grant's scope.
 *
 * Two kinds of role are here:
 *  - the implicit desk agents, held by ordinary employees who happen to sit on a desk team (ADR 0028, 0030): nothing is kept;
 *  - the narrow administrator roles (ADR 0023), which keep only what their own screens read.
 *
 * Deliberately NOT here: `manager`, `department_head`, `hr_manager`, `hr_admin`, `org_owner`, `auditor` — people-data roles, whose
 * Employee-base reach is the point of the role.
 *
 * Own-record access is never affected: a person reaches their own record through the `employee` grant (and `decide` lets any grant
 * act on the person's own record). A company that wants one of these people to see more gives them a people-data role.
 */
const SELF_CAPPED_BASE_ROLES: Readonly<Record<string, ReadonlySet<Permission>>> = Object.freeze({
  helpdesk_agent: new Set<Permission>(),
  travel_desk_agent: new Set<Permission>(),
  helpdesk_manager: new Set<Permission>(),
  travel_desk_manager: new Set<Permission>(),
  it_admin: new Set<Permission>(),
  recruiter: new Set<Permission>(),
  // Payroll needs who works where, their leave and attendance for loss of pay, and expense reimbursements.
  payroll_admin: new Set<Permission>([
    'employee:read',
    'employment:read',
    'leave:read',
    'leave.balance:read',
    'attendance:read',
    'expense:read',
  ]),
  finance: new Set<Permission>(['expense:read', 'asset:read']),
  expense_manager: new Set<Permission>(['expense:read']),
  // Running review cycles and calibration reads goals, reviews and improvement plans across the company.
  performance_admin: new Set<Permission>(['review:read', 'goal:read', 'improvement_plan:read', 'skill:read']),
  // Approving timesheets and work plans, and administering projects, reads them across people.
  project_admin: new Set<Permission>(['project:read', 'project.task:read', 'timesheet:read', 'work_plan:read']),
  asset_manager: new Set<Permission>(['asset:read']),
});

/**
 * The scope at which `grant` applies to THIS permission: its own, unless the role's Employee base is capped at self (above).
 * Anything that reads `grant.scope` to decide how far a permission reaches must go through this, not read the scope directly —
 * `decide` does, and so do the list routes that build their own "whose rows" filter.
 */
export function grantScopeFor(grant: Pick<Grant, 'role' | 'scope'>, permission: Permission): Scope {
  const kept = SELF_CAPPED_BASE_ROLES[grant.role];
  if (kept && grant.scope !== 'self' && ROLE_PERMISSIONS.employee.has(permission) && !kept.has(permission)) {
    return 'self';
  }
  return grant.scope as Scope;
}

/** The grant as it applies to THIS permission (see `grantScopeFor`). */
function effectiveGrant(grant: Grant, permission: Permission): Grant {
  const scope = grantScopeFor(grant, permission);
  return scope === grant.scope ? grant : { ...grant, scope, scopeId: null };
}

function getRolePermissions(
  actor: Actor,
  role: string,
): ReadonlySet<Permission> | readonly Permission[] | undefined {
  if (actor.rolePermissions && role in actor.rolePermissions) {
    return actor.rolePermissions[role];
  }
  return ROLE_PERMISSIONS[role as Role];
}

/**
 * Does `role` carry `permission` FOR THIS ACTOR'S ORGANISATION? The organisation's own role rows
 * (`actor.rolePermissions`, loaded per request) win over the code defaults, so a permission an org
 * switched on or off on the Roles & Permissions page counts here. Every "does this role grant X"
 * question must come through this function — reading `ROLE_PERMISSIONS` directly ignores the org's
 * choices (the `canAtDepartmentOrWider` bug fixed with ADR 0023).
 */
export function roleHasPermission(actor: Actor, role: string, permission: Permission): boolean {
  const perms = getRolePermissions(actor, role);
  if (!perms) return false;
  if (perms instanceof Set) return perms.has(permission);
  if (Array.isArray(perms)) return perms.includes(permission);
  return (perms as ReadonlySet<Permission>).has(permission);
}

/**
 * `.own` permissions (`payslip.own:read`, `compensation.own:read`, ...) are about the actor's OWN
 * record and nobody else's, whatever scope the role granting them is held at. Every role spreads
 * the employee set, so a manager holds `compensation.own:read` at `reporting_tree` scope; without
 * this rule that grant would cover every report's salary — the very thing the own/other split
 * (D1, ADR 0023) exists to separate. The converse also holds: against one's own record, any grant
 * carrying the permission is enough, whatever its scope — see decide().
 */
function isOwnPermission(permission: Permission): boolean {
  return permission.split(':')[0]!.split('.').includes('own');
}

// ---------------------------------------------------------------------------
// Separation of duties
// ---------------------------------------------------------------------------

/**
 * Permissions that are never satisfiable against the actor's OWN record, whatever scope they are
 * held at.
 *
 * WHY THIS CANNOT BE EXPRESSED WITH SCOPE. Scope widens; it does not carve out. An HR admin holds
 * `payroll.tax.proof:verify` at `org` scope, and `org` covers every person in the org — including
 * themselves. So "HR verifies proofs, but not their own" is not a narrower scope, it is a hole in
 * a wide one, and the only place to put it is here. Putting it in the route instead would mean
 * every future caller (a bulk verify, an AI tool, the mobile app) has to remember the same rule.
 *
 * An HR admin's own declaration is not blocked — they maintain it through self-service with
 * `payroll.tax.own:manage` like anyone else. What is blocked is being both sides of its review.
 */
const SELF_EXCLUDED_PERMISSIONS: ReadonlySet<Permission> = new Set<Permission>([
  'payroll.tax.proof:verify',
  // Same shape, same reason. A payroll administrator who disputes their own payslip raises a query
  // through `payroll.query.own:manage` like everyone else; what they may not do is issue the
  // official answer to it and close it. The ticket routes to a colleague instead.
  'payroll.query:resolve',
  // Money decisions about oneself (ADR 0015). The owner holds all four at org scope; the org covers the
  // owner's own settlement, hold, off-cycle payment and salary transfer, and none of those may be
  // approved or reconciled by the person they pay.
  'fnf:approve',
  'payroll.hold:approve',
  'payroll.offcycle:approve',
  'payroll.payment:reconcile',
  // Training (plan §8): nobody releases their own clock-out block, waives their own webcam
  // requirement, revokes their own certificate, or views their own webcam footage.
  'learning.override:execute',
  'learning.recording:view',
  // Performance (perf remediation #8, R2): nobody rates, calibrates, decides an appeal on, sets a
  // merit increment for, approves the merit budget covering, opens or runs a PIP on, or approves a
  // goal of THEMSELVES. An HR admin holds every one of these at org scope, which covers their own
  // review, appeal, recommendation, plan and goals. Only the deciding/writing permissions are
  // here: every `:read` stays satisfiable, so nobody loses sight of their own record.
  //
  // These fire only when the target names the subject (`target.personId`). Where a route's target
  // is not person-shaped (a calibration session, a merit cycle), the route checks each record it
  // touches itself, with `isSelfExcluded` below — see calibration.ts and merit.ts.
  'review.manager:submit',
  'review.calibrate:execute',
  'review:appeal:approve',
  'review:appeal:reject',
  'review:appeal:manage',
  'merit:submit',
  'merit:approve',
  'merit:reject',
  'improvement_plan:manage',
  'goal:approve',
]);

/**
 * Whether `permission` is refused on this target because the target is the actor's own record (see
 * SELF_EXCLUDED_PERMISSIONS). `decide()` applies the same rule; this is for a route that authorises
 * a container (a session, a cycle, a workflow step) and must still refuse each person-record inside
 * it that belongs to the actor.
 */
export function isSelfExcluded(
  actor: Pick<Actor, 'personId'>,
  permission: Permission,
  target: Pick<ResourceTarget, 'personId'>,
): boolean {
  return (
    SELF_EXCLUDED_PERMISSIONS.has(permission) &&
    !!target.personId &&
    target.personId === actor.personId
  );
}

/** The reason `decide()` gives for a self-excluded permission — one wording everywhere. */
export function selfExclusionReason(permission: Permission): string {
  return `'${permission}' can never be exercised on one's own record`;
}

// ---------------------------------------------------------------------------
// The decision
// ---------------------------------------------------------------------------

/**
 * READ-ONLY RULE (expired trial, unsigned agreement). A permission is `resource:action`. Under
 * `actor.readOnly` it is DENIED unless one of:
 *   - the action is `read`, `export` (retrieving one's own data) or `use` (`ai.copilot:use`; any
 *     write the copilot proposes still executes through a route that `can()` gates); or
 *   - the resource is `agreement` or `org.billing`, so the customer can sign and pay.
 * Deny-by-default for every other verb (create/update/delete/manage/approve/submit/request/...)
 * is deliberate: a newly added verb is blocked until someone consciously allows it.
 * Login/refresh/logout/change-password are not `can()`-gated, so they are unaffected.
 */
const READ_ONLY_ALLOWED_ACTIONS: ReadonlySet<string> = new Set(['read', 'export', 'use']);
const READ_ONLY_EXEMPT_RESOURCES: ReadonlySet<string> = new Set(['agreement', 'org.billing']);

function isBlockedWhenReadOnly(permission: Permission): boolean {
  const i = permission.indexOf(':');
  const resource = i === -1 ? permission : permission.slice(0, i);
  const action = i === -1 ? '' : permission.slice(i + 1);
  if (READ_ONLY_EXEMPT_RESOURCES.has(resource)) return false;
  return !READ_ONLY_ALLOWED_ACTIONS.has(action);
}

/**
 * Full decision with the reason, for audit rows and for debugging.
 * Most call sites want `can()` below.
 */
export function decide(actor: Actor, permission: Permission, target: ResourceTarget): Decision {
  // Hard stop before anything else. RLS would also refuse, but an explicit denial here produces a
  // comprehensible 403 instead of a mysteriously empty result set.
  if (target.orgId !== actor.orgId) {
    return { allowed: false, reason: 'cross-organisation access is never permitted' };
  }

  if (actor.readOnly && isBlockedWhenReadOnly(permission)) {
    return {
      allowed: false,
      reason: 'trial expired: organisation is read-only until the agreement is signed',
    };
  }

  // Separation of duties, before scope is even considered. See SELF_EXCLUDED_PERMISSIONS.
  if (SELF_EXCLUDED_PERMISSIONS.has(permission) && target.personId === actor.personId) {
    return { allowed: false, reason: selfExclusionReason(permission) };
  }

  // The mirror image: an `.own` permission is ONLY satisfiable against one's own record.
  if (isOwnPermission(permission) && (!target.personId || target.personId !== actor.personId)) {
    return {
      allowed: false,
      reason: `'${permission}' covers only one's own record`,
    };
  }

  const ownRecord = isOwnPermission(permission);
  let heldButOutOfScope = false;

  for (const grant of actor.grants) {
    if (!roleHasPermission(actor, grant.role, permission)) continue;

    // For an `.own` permission the check above has already pinned the target to the actor, so the
    // grant's scope has nothing left to decide. Without this a person whose only grant is `manager`
    // at `reporting_tree` (most seeded managers hold no separate `employee` grant) would be refused
    // their own payslip and salary: `reporting_tree` covers the people below them, never themselves.
    if (ownRecord || scopeCovers(actor, effectiveGrant(grant, permission), target)) {
      return {
        allowed: true,
        via: grant,
        reason: `role '${grant.role}' at scope '${grant.scope}'`,
      };
    }
    heldButOutOfScope = true;
  }

  return {
    allowed: false,
    reason: heldButOutOfScope
      ? `permission '${permission}' is held, but not over this record`
      : `permission '${permission}' is not granted by any role held`,
  };
}

/** The everyday check. */
export function can(actor: Actor, permission: Permission, target: ResourceTarget): boolean {
  return decide(actor, permission, target).allowed;
}

/**
 * "Could this actor do X to anything at all?"
 *
 * For navigation and menu gating ONLY — showing a Payroll tab to someone who administers payroll
 * for one entity. **Never** use it to authorise an operation: it deliberately ignores scope, so it
 * answers a strictly weaker question than `can()`.
 */
export function canAnywhere(actor: Actor, permission: Permission): boolean {
  // The read-only rule applies here too: this is what the API's route gate and most screens ask,
  // so a read-only organisation must not pass it for a write (see READ-ONLY RULE above).
  if (actor.readOnly && isBlockedWhenReadOnly(permission)) return false;
  return actor.grants.some((g) => roleHasPermission(actor, g.role, permission));
}

/**
 * "Does this actor hold `permission` at department scope or wider (i.e. `department` or `org`)?"
 *
 * For gating an action or a view over a SHARED, department-or-company-wide resource — creating a
 * department/company Objective, showing every department's cards on a personal overview — where
 * `canAnywhere` is too permissive: it also passes for a `reporting_tree`/`direct_reports`/`self`
 * grant, none of which cover an artifact that belongs to a whole department rather than to one
 * person's reporting line. A plain manager holding `goal:manage` only at `reporting_tree` scope
 * must NOT see a button whose action always 403s for them — that mismatch is exactly the bug this
 * function exists to close. Still a courtesy check, not the authorization boundary: the route
 * re-verifies per department/org before writing anything.
 */
export function canAtDepartmentOrWider(actor: Actor, permission: Permission): boolean {
  if (actor.readOnly && isBlockedWhenReadOnly(permission)) return false;
  // Through roleHasPermission, like decide(): an org that removed the permission from a role must
  // not keep seeing the button, and one that added it must see it.
  return actor.grants.some(
    (g) =>
      (g.scope === 'org' || g.scope === 'department') &&
      roleHasPermission(actor, g.role, permission),
  );
}

/**
 * Every permission the actor holds somewhere, minus what the read-only rule blocks. For seeding a
 * client-side capability map.
 */
export function permissionsHeld(actor: Actor): Set<Permission> {
  const held = new Set<Permission>();
  for (const grant of actor.grants) {
    const perms = getRolePermissions(actor, grant.role) ?? [];
    for (const permission of perms) {
      if (actor.readOnly && isBlockedWhenReadOnly(permission)) continue;
      held.add(permission);
    }
  }
  return held;
}

/**
 * What each role the actor holds means in THIS organisation, keyed by role — custom roles and
 * edited built-in roles included. The API sends it with `/me` so a client can build an `Actor`
 * whose `rolePermissions` is set and run this same pure `can()`; without it the client can only
 * fall back to the code defaults, which know nothing about a role an admin created.
 */
export function rolePermissionsHeld(actor: Actor): Record<string, Permission[]> {
  // A Map, then fromEntries: a role key is data, so it must not be able to hit `__proto__`.
  const byRole = new Map<string, Permission[]>();
  for (const grant of actor.grants) {
    if (byRole.has(grant.role)) continue;
    byRole.set(grant.role, [...(getRolePermissions(actor, grant.role) ?? [])]);
  }
  return Object.fromEntries(byRole);
}

/** Convenience for self-service routes, where the target is always the actor. */
export function selfTarget(actor: Actor): ResourceTarget {
  return { orgId: actor.orgId, personId: actor.personId };
}
